import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Map as LMap, Marker } from 'react-leaflet';
import Map from 'views/components/Map/ReducedMap';

import { useMapSettings } from 'hooks/useMapSettings';

import { Controls } from './Controls';
import { Layers } from './Layers';

import {
  AgenciesFilters,
  BCRegion,
  DetailsInfoState,
  GeolocationData,
  MapElements,
  MapLayers,
  MapModes,
  OnSearchVehicle,
  Props,
} from './MapPage.types';
import { Styled } from './MapPage.styles';

import { mapContext } from './mapContext';
import {
  IRoute,
  parseRoutes,
} from '../../Video/OnlineWatching/utils/parseRoutes';
import { getRoutesRequest } from 'api/video/route';
import { EntitiesSearch } from './EntitiesSearch';
import { Vehicle } from 'api/mobile/filters';
import { FeatureGroup, Icon } from 'leaflet';
import { ICluster } from 'views/components/Video/MapComponent';
import { Cluster } from './Cluster';
import {
  getInitialLayers,
  LOCAL_STORAGE_BC_LAYERS_KEY,
  LOCAL_STORAGE_MAP_LAYERS_KEY,
} from './helpers';
import { Route as RouteElement } from './EntitiesSearch/Modes/Modes.types';
import { securePostRequest, secureRequest } from 'utils/request';
import { DetailsInfo } from './DetailsInfo';
import { MapObjects } from './MapObjects';
import { useHistory, useLocation } from 'react-router';
import { useUrlDependedBoolean } from 'hooks/useUrlDependedBoolean';
import { Page } from '../components/Page';
import { Capacitor, PermissionType, Plugins } from '@capacitor/core';
import {
  BackButton,
  LogoutButton,
  NavigationButton,
} from '../components/NavigationButton';
import userIcon from 'images/user.svg';
import { useVehiclesFromEventSource } from 'hooks/useVehiclesEventSource';
import { fitMap, parsePoint } from '../../../../utils/map';

const { Geolocation, Permissions } = Plugins;

export const MapPage: React.FC<Props> = ({ mode, current_dep }) => {
  const [title, setTitle] = useState('');

  useEffect(() => {
    switch (mode) {
      case MapModes.default:
        setTitle('Карта');
        break;
      case MapModes.departmentControl:
        setTitle('Текущий процесс контроля');
        break;
      case MapModes.currentRegion:
        setTitle('Районы контроля');
        break;
    }
  }, [mode]);

  const [routes, setRoutes] = useState<RouteElement[]>([]);

  const [attemptedToGetLocation, setAttemptedToGetLocation] = useState(false);

  const { handleChangeMapSettings, mapSettings } = useMapSettings();
  const mapRef = useRef<LMap>(null);

  const { search } = useLocation();

  const isBc = mode !== MapModes.default;

  const [layers, setLayers] = useState<MapLayers>(getInitialLayers(mode));

  const [showLayersPopup, toggleShowLayersPopup] = useUrlDependedBoolean(
    'showLayers'
  );

  const [
    showEntitiesSearchPopup,
    toggleShowEntitiesSearchPopup,
  ] = useUrlDependedBoolean('showEntitiesSearchPopup');
  const [detailsInfo, setDetailsInfo] = useState<DetailsInfoState>(null);

  const onChangeDetailsInfo = (details: DetailsInfoState) => {
    // details.type
  };

  const [filteredVehicle, setFilteredVehicle] = useState<Vehicle | null>(null);
  const [filteredRoutes, setFilteredRoutes] = useState<number[]>([]);
  const [clusters, setClusters] = useState<ICluster[]>([]);
  const [BCRegions, setBCRegions] = useState<BCRegion[]>([]);
  const [agenciesFilters, setAgenciesFilters] = useState<AgenciesFilters>({
    agencies: [],
    departments: [],
    depots: [],
  });
  const [selectedAgencies, setSelectedAgencies] = useState<number[]>([]);

  const [mapElements, setMapElements] = useState<MapElements | null>(null);

  const [userCoordinates, setUserCoordinates] = useState<
    [number, number] | null
  >(null);

  const onToggleLayer = (layer: string) => {};

  useEffect(() => {
    const getAgenciesFilters = async () => {
      const filters = await secureRequest<AgenciesFilters>(
        '/api/v1/map/branchagency'
      );
      if (filters) {
        setAgenciesFilters({
          ...filters,
          agencies: filters.agencies.map((ag) => ({
            ...ag,
            id: ag.agency_id,
          })),
          depots: filters.depots.map((dp) => ({
            ...dp,
            title: dp.depot_name,
          })),
          departments: filters.departments.map((dep) => ({
            ...dep,
            title: dep.td_name,
          })),
        });
      }
    };
    getAgenciesFilters();
  }, []);

  useEffect(() => {
    if (isBc) {
      const getBcData = async () => {
        const data: BCRegion[] = await securePostRequest(
          'api/v1/pass/departmentcontroll/map'
        )({
          department_ids:
            mode === MapModes.currentRegion ? [current_dep] : null,
        });
        setBCRegions(data);
      };
      getBcData();
    }
  }, [current_dep, isBc, mode]);

  useEffect(() => {
    const dep_id = new URLSearchParams(search).get('dep_id');
    if (dep_id) {
      mapRef.current?.leafletElement.eachLayer((layer) => {
        const id: string | undefined = (layer as any)?.options?.id;
        if (
          id?.includes('REGION_') &&
          parseInt(id?.replace(/\D*/, '')) === parseInt(dep_id)
        ) {
          mapRef.current?.leafletElement.fitBounds(
            (layer as FeatureGroup).getBounds()
          );
        }
      });
    }
  }, [BCRegions, search]);

  const centerScreenOnGeoData = useCallback(
    (geolocationData: GeolocationData) => {
      const map = mapRef.current?.leafletElement;
      if (map && geolocationData) {
        const {
          coords: { latitude, longitude },
        } = geolocationData;

        map.setView([latitude, longitude], map.getZoom());
      }
    },
    []
  );

  const getUserCoordinates = useCallback(async (): Promise<GeolocationData> => {
    const { state } = await Permissions.query({
      name: PermissionType.Geolocation,
    });
    if (state === 'granted' && Capacitor.platform !== 'ios') {
      return await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (data) => {
            resolve(data);
          },
          (error) => {
            reject(error);
          }
        );
      });
    }
    return Geolocation.getCurrentPosition();
  }, []);

  const centerOnUser = useCallback(async () => {
    const data = await getUserCoordinates();
    centerScreenOnGeoData(data);
    setUserCoordinates([data.coords.latitude, data.coords.longitude]);
  }, [centerScreenOnGeoData, getUserCoordinates]);

  const handleClickCenterOnUser = useCallback(async () => {
    const data = userCoordinates
      ? {
          coords: {
            latitude: userCoordinates[0],
            longitude: userCoordinates[1],
          },
        }
      : await getUserCoordinates();
    centerScreenOnGeoData(data);
  }, [centerScreenOnGeoData, getUserCoordinates, userCoordinates]);

  useEffect(() => {
    if (current_dep) {
      const found = BCRegions.find(({ td_id }) => td_id === current_dep);
      if (found) {
        setFilteredRoutes(found.routes.map(({ route_id }) => route_id));
      }
    }
  }, [BCRegions, centerOnUser, current_dep]);

  useEffect(() => {
    const dep_id = new URLSearchParams(search).get('dep_id');
    if (!dep_id) {
      centerOnUser();
    }
  }, [centerOnUser, isBc, search]);

  useEffect(() => {
    localStorage.setItem(
      isBc ? LOCAL_STORAGE_BC_LAYERS_KEY : LOCAL_STORAGE_MAP_LAYERS_KEY,
      JSON.stringify(layers)
    );
  }, [isBc, layers]);

  useEffect(() => {
    const callback = ({ coords }: GeolocationData, err?: any) => {
      console.log('--------');
      console.log({ err });
      console.log('--------');
      setUserCoordinates([coords.latitude, coords.longitude]);
    };

    if (Geolocation?.watchPosition) {
      const watchId = Geolocation?.watchPosition({}, callback);
      return () => {
        Geolocation?.clearWatch({
          id: watchId,
        });
      };
    } else {
      const watchId = navigator.geolocation.watchPosition(
        callback,
        (error) => {
          console.log('-----error-----');
          console.log({ error });
          console.log('-----error-----');
        },
        {}
      );
      return () => {
        navigator.geolocation.clearWatch(watchId);
      };
    }
  }, [attemptedToGetLocation, centerOnUser]);

  const getMapElements = useCallback(async () => {
    const respondedRoutes: IRoute[] = await getRoutesRequest(null);
    if (respondedRoutes) {
      const { minimizedStop, initialRoutes, routes, stops } = parseRoutes(
        respondedRoutes
      );
      setMapElements({
        selectedRoutes: routes,
        minimizedStops: minimizedStop,
        selectedStops: stops,
        initialRoutes,
      });
    }
  }, []);

  useEffect(() => {
    if (mapElements === null) {
      getMapElements();
    }
  }, [getMapElements, mapElements]);

  useVehiclesFromEventSource({
    filteredVehicles: filteredVehicle ? [filteredVehicle.id] : null,
    mapSettings,
    setClusters,
  });

  useEffect(() => {
    const getFilters = async () => {
      const filters = await secureRequest('/api/v1/map/mobile/filters/map');
      if (filters) {
        setRoutes(filters.routes);
      }
    };
    getFilters();
  }, [setRoutes]);

  const onSearchVehicles = useCallback<OnSearchVehicle>(
    async (vehicle) => {
      setFilteredVehicle(vehicle);
      if (vehicle) {
        const info = await secureRequest<Vehicle>(
          '/api/v1/map/online/' + vehicle.id
        );
        if (info) {
          const center = parsePoint(info.center);
          if (center) {
            mapRef.current?.leafletElement.setView(center, 16);
          }
        }
      }
    },
    []
  );

  const onSearchRoutes = useCallback(
    (routeIds: number[]) => {
      setFilteredRoutes(routeIds);
      if (mapElements?.selectedRoutes) {
        fitMap({
          map: mapRef.current,
          latLng: mapElements.selectedRoutes
            .filter(({ id }) => routeIds.includes(id))
            .flatMap(({ position }) => position),
        });
      }
    },
    [mapElements?.selectedRoutes]
  );

  const onSearchAgency = useCallback(
    (agencyIds: number[]) => {
      setSelectedAgencies(agencyIds);
      fitMap({
        map: mapRef.current,
        latLng: agenciesFilters.agencies
          .filter(({ agency_id }) => agencyIds.includes(agency_id))
          .map(({ ag_lat, ag_lon }) => ({
            lat: ag_lat,
            lng: ag_lon,
          })),
      });
    },
    [agenciesFilters.agencies]
  );

  const history = useHistory();

  const handleClickNavButton = () => {
    history.push('/bc');
  };

  const headerButton = isBc ? (
    <BackButton onClick={handleClickNavButton} />
  ) : (
    <NavigationButton />
  );

  return (
    <mapContext.Provider
      value={{
        layers,
        toggleShowLayersPopup,
        showLayersPopup,
        centerOnUser: handleClickCenterOnUser,
        getMapElements,
        toggleShowEntitiesSearchPopup,
        showEntitiesSearchPopup,
        onSearchVehicles,
        mapSettings: {
          handleChangeMapSettings,
          mapSettings,
        },
        clusters,
        setLayers,
        setFilteredRoutes: onSearchRoutes,
        filteredVehicle,
        setAgenciesFilters,
        agenciesFilters,
        setSelectedAgencies: onSearchAgency,
        detailsInfo,
        setDetailsInfo,
        selectedAgencies,
        filteredRoutes,
        mapElements,
        mapRef,
        setRoutes,
        routes,
        BCRegions,
        isBc,
      }}
    >
      <DetailsInfo />
      <Layers />
      <EntitiesSearch />
      <Page
        backAction={handleClickNavButton}
        title={title}
        primaryButtonNode={headerButton}
        actionButton={mode === MapModes.default && <LogoutButton />}
      >
        <Styled.MapWrapper>
          <Controls />
          <Map
            mapRef={mapRef}
            ref={mapRef}
            mapSettings={mapSettings}
            onChangeMapSettings={handleChangeMapSettings}
          >
            <Cluster />
            {userCoordinates && (
              <Marker
                icon={
                  new Icon({
                    iconUrl: userIcon,
                    iconSize: [24, 24],
                  })
                }
                position={userCoordinates}
              />
            )}
            <MapObjects />
          </Map>
        </Styled.MapWrapper>
      </Page>
    </mapContext.Provider>
  );
};
